/**
 * Routes this app actually serves. Everything else same-origin — the universal
 * nav's `/app/home`, `/apps`, `/app/connections`, and the invitation decline
 * page's dashboard link — is served by the monolith.
 *
 * React Aria hands every same-origin href to `DesignSystemProvider`'s
 * `navigate`, so without this allowlist monolith paths would go through
 * `router.push`, which costs a wasted route and data resolution before Next
 * falls back to a hard navigation. The `Link` wrapper this replaced used the
 * same allowlist to choose client-side routing versus a plain anchor.
 */
const OWNED_ROUTE_PREFIXES = ['/app/login', '/app/invitation', '/sign-up'];

const PARSE_BASE = 'https://placeholder.invalid';

/**
 * `new URL` rather than `URL.parse`: the static is Chrome 126 / Safari 18, and
 * with no browserslist configured Next targets Chrome 64 / Safari 12. It is a
 * runtime method, so transpiling does not cover it and Next does not polyfill
 * it — on an older browser it would throw on every press. The try/catch is
 * what `URL.parse` does internally anyway.
 */
function parsePath(path: string) {
  try {
    return new URL(path, PARSE_BASE);
  } catch {
    return null;
  }
}

export function isOwnedRoute(path: string): boolean {
  // React Aria passes the href through verbatim, so parse it to drop the
  // query and hash and to resolve any relative segments: `/sign-up?next=…` and
  // `/app/login?invitation=…` (both built by `InvitationError`) are owned
  // routes and would otherwise fall through to a full page load, while
  // `/sign-up/../../elsewhere` only looks like one.
  const url = parsePath(path);
  if (!url) {
    return false;
  }

  // protocol-relative (`//other.com/sign-up`) or absolute URLs resolve to a
  // different origin and aren't ours
  if (url.origin !== PARSE_BASE) {
    return false;
  }

  return OWNED_ROUTE_PREFIXES.some(
    prefix => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)
  );
}
